import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Category, Wallpaper } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, ChevronRight, ImageOff, Monitor, Smartphone } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { EmptyState } from '@/components/generals/empty-state'
import { SectionHeading } from '@/components/generals/section-heading'
import { CategoryCard } from '@/components/wallpapers/category-card'
import { WallpaperMasonry } from '@/components/wallpapers/wallpaper-masonry'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { toCssColor } from '@/lib/colors'
import { pluralize } from '@/lib/format'
import { buildBrowseHref, DEFAULT_FILTERS } from '@/lib/browse'

import {
    browseWallpapers,
    getAllCategorySlugs,
    getCategories,
    getCategoryBySlug
} from '@/lib/wallpapers'

import { REVALIDATE_SECONDS } from '@/constants/site'

interface CategoryPageProps {
    category: Category
    wallpapers: Array<Wallpaper>
    total: number
    others: Array<Category>
}

const CategoryPage: FC<CategoryPageProps> = ({ category, wallpapers, total, others }): ReactNode => {
    const categoryFilters = { ...DEFAULT_FILTERS, category: category.slug }

    return (
        <Layout>
            <Seo
                title={`${category.name} wallpapers`}
                description={category.description || `Free ${category.name.toLowerCase()} wallpapers for your desktop and phone.`}
                ogImage={category.cover_image ?? undefined}
            />

            <section
                id='category-header'
                className='relative isolate overflow-hidden border-b border-white/[0.06]'
                style={{ backgroundColor: toCssColor(category.cover_color) }}
            >
                {category.cover_image && (
                    <Image
                        src={category.cover_image}
                        alt=''
                        fill
                        preload
                        sizes='100vw'
                        className='-z-20 object-cover'
                    />
                )}

                <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-t from-background via-background/75 to-background/30' />

                <Container className='pt-8 pb-12 sm:pt-10 sm:pb-16'>
                    <nav aria-label='Breadcrumb'>
                        <ol className='flex flex-wrap items-center gap-1.5 text-sm text-white/70'>
                            <li>
                                <Link href='/' className='rounded transition hover:text-white focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'>
                                    Home
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li>
                                <Link href='/categories' className='rounded transition hover:text-white focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'>
                                    Categories
                                </Link>
                            </li>

                            <li aria-hidden='true'>
                                <ChevronRight className='size-3.5' />
                            </li>

                            <li aria-current='page' className='text-white'>
                                <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                    {category.name}
                                </span>
                            </li>
                        </ol>
                    </nav>

                    <div className='mt-16 max-w-3xl sm:mt-24'>
                        <p className='text-xs font-semibold tracking-[0.16em] text-brand uppercase'>
                            Category
                        </p>

                        <h1
                            className='mt-3 font-heading text-5xl leading-[1.02] tracking-tight text-white sm:text-7xl'
                            data-mdlfy-cms={`categories:${category.id}:name`}
                        >
                            {category.name}
                        </h1>

                        {category.description && (
                            <p
                                className='mt-4 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg'
                                data-mdlfy-cms={`categories:${category.id}:description`}
                            >
                                {category.description}
                            </p>
                        )}

                        <div className='mt-6 flex flex-wrap items-center gap-3'>
                            <span className='inline-flex h-9 items-center rounded-full border border-white/15 bg-black/30 px-4 text-sm font-medium text-white/90 backdrop-blur-md'>
                                <span>{total}</span>
                                {' '}
                                {pluralize(total, 'wallpaper')}
                            </span>

                            <Link
                                href={buildBrowseHref(categoryFilters, { device: 'desktop' })}
                                className='inline-flex h-9 items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 text-sm font-medium text-white/90 backdrop-blur-md transition hover:bg-black/50 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                            >
                                <Monitor aria-hidden='true' className='size-4' />
                                Desktop only
                            </Link>

                            <Link
                                href={buildBrowseHref(categoryFilters, { device: 'phone' })}
                                className='inline-flex h-9 items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 text-sm font-medium text-white/90 backdrop-blur-md transition hover:bg-black/50 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                            >
                                <Smartphone aria-hidden='true' className='size-4' />
                                Phone only
                            </Link>
                        </div>
                    </div>
                </Container>
            </section>

            <section id='category-wallpapers' aria-labelledby='category-wallpapers-title' className='pt-10 sm:pt-12'>
                <Container>
                    <h2 id='category-wallpapers-title' className='sr-only'>
                        Wallpapers in this category
                    </h2>

                    {wallpapers.length > 0 ? (
                        <>
                            <WallpaperMasonry wallpapers={wallpapers} />

                            {total > wallpapers.length && (
                                <div className='mt-10 flex justify-center'>
                                    <Link
                                        href={buildBrowseHref(categoryFilters)}
                                        className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'rounded-full')}
                                    >
                                        {'Browse all '}
                                        <span>{total}</span>
                                        <ArrowRight aria-hidden='true' />
                                    </Link>
                                </div>
                            )}
                        </>
                    ) : (
                        <EmptyState
                            icon={<ImageOff aria-hidden='true' className='size-5' />}
                            title='Nothing here yet'
                            description='Wallpapers for this category are on their way. Browse the rest of the collection in the meantime.'
                            action={(
                                <Link href='/wallpapers' className={buttonVariants({ size: 'lg' })}>
                                    Browse all wallpapers
                                </Link>
                            )}
                        />
                    )}
                </Container>
            </section>

            {others.length > 0 && (
                <section id='more-categories' aria-labelledby='more-categories-title' className='pt-20 sm:pt-24'>
                    <Container>
                        <SectionHeading
                            id='more-categories-title'
                            eyebrow='Keep exploring'
                            title='More categories'
                            action={(
                                <Link href='/categories' className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'rounded-full')}>
                                    All categories
                                    <ArrowRight aria-hidden='true' />
                                </Link>
                            )}
                        />

                        <ul className='mt-10 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4'>
                            {others.map(other => (
                                <li key={other.id}>
                                    <CategoryCard category={other} sizes='(min-width: 768px) 25vw, 50vw' />
                                </li>
                            ))}
                        </ul>
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getAllCategorySlugs()

    return {
        paths: slugs.map(slug => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<CategoryPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const category = slug ? await getCategoryBySlug(slug) : null

    if (!category) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const [result, categories] = await Promise.all([
        browseWallpapers({ ...DEFAULT_FILTERS, category: category.slug }),
        getCategories()
    ])

    return {
        props: {
            category,
            wallpapers: result.wallpapers,
            total: result.total,
            others: categories.filter(other => other.id !== category.id && other.wallpaper_count > 0).slice(0, 4)
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default CategoryPage
